import type { ChangeEvent, FC, FormEvent, MouseEvent, ReactNode } from 'react'
import type { RequestTopic } from '@/lib/app-store'

import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Check, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { setAppState, useAppState } from '@/lib/app-store'
import { Bracket } from '@/components/landing/primitives'
import { requestTopics, sectors } from '@/constants/content'

type Status = 'idle' | 'sending' | 'sent' | 'error'

interface FormValues {
    name: string
    email: string
    company: string
    sector: string
    location: string
    message: string
    topic: RequestTopic
    website: string
}

const EMPTY: FormValues = {
    name: '',
    email: '',
    company: '',
    sector: 'Energy',
    location: '',
    message: '',
    topic: 'survey',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']{2,}$/

const fieldClass = 'h-11 w-full border-b border-line bg-transparent text-[15px] tracking-[-0.01em] text-ink outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-pale focus:border-ink focus:shadow-[0_1px_0_0_#1d2a31] focus-visible:outline-none'

const RequestDialog: FC = (): ReactNode => {
    const open = useAppState(state => state.requestOpen)
    const topic = useAppState(state => state.requestTopic)
    const dialogRef = useRef<HTMLDialogElement>(null)
    const firstFieldRef = useRef<HTMLInputElement>(null)
    const [values, setValues] = useState<FormValues>(EMPTY)
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({})

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        if (open && !dialog.open) {
            setValues(current => ({ ...current, topic }))
            setStatus('idle')
            setMessage('')
            setErrors({})
            dialog.showModal()
            document.documentElement.classList.add('cursor-native')
            window.setTimeout(() => firstFieldRef.current?.focus(), 60)
        }

        if (!open && dialog.open) dialog.close()
    }, [open, topic])

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        const onClose = (): void => {
            document.documentElement.classList.remove('cursor-native')
            setAppState({ requestOpen: false })
        }

        dialog.addEventListener('close', onClose)

        return () => dialog.removeEventListener('close', onClose)
    }, [])

    const close = (): void => {
        dialogRef.current?.close()
    }

    const onBackdrop = (event: MouseEvent<HTMLDialogElement>): void => {
        if (event.target === dialogRef.current) close()
    }

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const value = event.target.value

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') return

        const nextErrors: Partial<Record<keyof FormValues, string>> = {}

        if (values.name.trim().length < 2) nextErrors.name = 'Please enter your name.'
        if (!EMAIL_PATTERN.test(values.email.trim())) nextErrors.email = 'Please enter a valid work email.'

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            setStatus('error')
            setMessage('Please check the highlighted fields.')

            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/survey-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => ({})) as { message?: string, errors?: Partial<Record<keyof FormValues, string>> }

            if (!response.ok) {
                setStatus('error')
                setMessage(data.message ?? 'Something went wrong. Please try again.')
                setErrors(data.errors ?? {})

                return
            }

            setStatus('sent')
            setValues(current => ({ ...EMPTY, topic: current.topic }))
        }
        catch {
            setStatus('error')
            setMessage('We could not reach our flight team. Check your connection and try again.')
        }
    }

    return (
        <dialog
            ref={dialogRef}
            onClick={onBackdrop}
            aria-labelledby='request-title'
            className='request-dialog m-auto max-h-[calc(100svh_-_24px)] w-[min(680px,calc(100vw_-_24px))] overflow-hidden rounded-[24px] border border-line bg-[#f8fafa] p-0 text-ink shadow-[0_40px_80px_-30px_rgba(29,42,49,0.45)]'
        >
            <div className='max-h-[calc(100svh_-_24px)] overflow-y-auto' data-lenis-prevent=''>
                <div className='flex items-start justify-between gap-6 border-b border-line px-6 pb-5 pt-6 md:px-9 md:pt-8'>
                    <div className='flex flex-col gap-3'>
                        <Bracket className='text-haze'>Survey request</Bracket>
                        <h2 id='request-title' className='text-[clamp(26px,3.4vw,38px)] font-light leading-[1.05] tracking-[-0.045em]'>
                            Tell us what to fly over.
                        </h2>
                    </div>

                    <button
                        type='button'
                        onClick={close}
                        aria-label='Close'
                        className='flex size-9 shrink-0 items-center justify-center rounded-full bg-[#e4e9eb] text-ink transition-colors hover:bg-[#d7dee1]'
                    >
                        <X aria-hidden='true' className='size-4' />
                    </button>
                </div>

                {status === 'sent' ? (
                    <div className='flex flex-col items-start gap-5 px-6 py-10 md:px-9 md:py-12' role='status'>
                        <span className='flex size-11 items-center justify-center rounded-full bg-ink text-white'>
                            <Check aria-hidden='true' className='size-5' />
                        </span>

                        <div className='flex flex-col gap-2'>
                            <p className='text-[24px] font-light tracking-[-0.04em]'>Request received.</p>
                            <p className='max-w-[440px] text-[15px] leading-relaxed text-[#66727a]'>
                                Our flight team replies within one business day with a flight plan, a date and the formats you will receive.
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={close}
                            className='mt-2 inline-flex h-12 items-center bg-ink px-6 text-[14px] tracking-[-0.01em] text-white transition-colors hover:bg-[#2b3b44]'
                        >
                            Back to the demo
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} noValidate className='flex flex-col gap-6 px-6 pb-7 pt-6 md:px-9 md:pb-9'>
                        <div className='grid gap-6 md:grid-cols-2'>
                            <label className='flex flex-col gap-1.5'>
                                <span className='label-mono text-[#66727a]'>Name *</span>
                                <input
                                    ref={firstFieldRef}
                                    type='text'
                                    name='name'
                                    autoComplete='name'
                                    value={values.name}
                                    onChange={update('name')}
                                    aria-invalid={Boolean(errors.name)}
                                    aria-describedby={errors.name ? 'request-name-error' : undefined}
                                    className={cn(fieldClass, errors.name && 'border-red-400')}
                                    placeholder='Jordan Ellis'
                                />
                                {errors.name && <span id='request-name-error' className='text-[12px] text-red-500'>{errors.name}</span>}
                            </label>

                            <label className='flex flex-col gap-1.5'>
                                <span className='label-mono text-[#66727a]'>Work email *</span>
                                <input
                                    type='email'
                                    name='email'
                                    autoComplete='email'
                                    value={values.email}
                                    onChange={update('email')}
                                    aria-invalid={Boolean(errors.email)}
                                    aria-describedby={errors.email ? 'request-email-error' : undefined}
                                    className={cn(fieldClass, errors.email && 'border-red-400')}
                                    placeholder='jordan@utility.com'
                                />
                                {errors.email && <span id='request-email-error' className='text-[12px] text-red-500'>{errors.email}</span>}
                            </label>

                            <label className='flex flex-col gap-1.5'>
                                <span className='label-mono text-[#66727a]'>Company</span>
                                <input
                                    type='text'
                                    name='company'
                                    autoComplete='organization'
                                    value={values.company}
                                    onChange={update('company')}
                                    className={fieldClass}
                                    placeholder='Northline Grid Operations'
                                />
                            </label>

                            <label className='flex flex-col gap-1.5'>
                                <span className='label-mono text-[#66727a]'>Site location</span>
                                <input
                                    type='text'
                                    name='location'
                                    value={values.location}
                                    onChange={update('location')}
                                    className={fieldClass}
                                    placeholder='Region, coordinates or address'
                                />
                            </label>

                            <label className='flex flex-col gap-1.5'>
                                <span className='label-mono text-[#66727a]'>Sector</span>
                                <select name='sector' value={values.sector} onChange={update('sector')} className={cn(fieldClass, 'cursor-pointer appearance-none')}>
                                    {sectors.map(sector => <option key={sector} value={sector}>{sector}</option>)}
                                </select>
                            </label>

                            <label className='flex flex-col gap-1.5'>
                                <span className='label-mono text-[#66727a]'>Topic</span>
                                <select name='topic' value={values.topic} onChange={update('topic')} className={cn(fieldClass, 'cursor-pointer appearance-none')}>
                                    {requestTopics.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                            </label>
                        </div>

                        <label className='flex flex-col gap-1.5'>
                            <span className='label-mono text-[#66727a]'>What should we inspect?</span>
                            <textarea
                                name='message'
                                rows={3}
                                value={values.message}
                                onChange={update('message')}
                                className={cn(fieldClass, 'h-auto min-h-[88px] resize-none py-2.5 leading-relaxed')}
                                placeholder='Asset type, approximate area and any deadlines.'
                            />
                        </label>

                        <label className='sr-only' aria-hidden='true'>
                            Website
                            <input type='text' name='website' tabIndex={-1} autoComplete='off' value={values.website} onChange={update('website')} />
                        </label>

                        <div className='flex flex-col gap-4 border-t border-line pt-6 md:flex-row md:items-center md:justify-between'>
                            <p className={cn('text-[13px] leading-relaxed', status === 'error' ? 'text-red-600' : 'text-[#66727a]')} role={status === 'error' ? 'alert' : undefined}>
                                {status === 'error' && message ? message : 'We reply within one business day.'}
                            </p>

                            <button
                                type='submit'
                                disabled={status === 'sending'}
                                className='group inline-flex h-12 items-center justify-center gap-3 bg-ink px-6 text-[14px] tracking-[-0.01em] text-white transition-colors hover:bg-[#2b3b44] disabled:cursor-wait disabled:opacity-80'
                            >
                                {status === 'sending' ? (
                                    <>
                                        <Loader2 aria-hidden='true' className='size-4 animate-spin' />
                                        Sending request
                                    </>
                                ) : (
                                    <>
                                        Send request
                                        <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </dialog>
    )
}

export default RequestDialog
